import { useState, useEffect } from 'react';
import { api } from '../lib/api.js';
import { fmtDate, todayStr } from '../lib/format.js';
import { ProfileSection } from './ProfileSection.jsx';

// Coaching log card — manager view only. Records improvement-area
// conversations + warnings + PIPs. CJ feedback 2026-05-08: this is the
// UI claim defense paper trail. NEVER visible to the staff member.
export function CoachingLogCard({ currentUser, targetUserId, targetName }) {
  const headers = { 'x-user-id': String(currentUser.id) };
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({
    conversation_date: todayStr(),
    topic: '', summary: '', action_items: '', severity: 'coaching',
    step_label: '', follow_up_date: '',
  });
  const [savingMsg, setSavingMsg] = useState('');

  async function load() {
    setLoading(true);
    try {
      const r = await api('GET', `/api/admin/users/${targetUserId}/coaching-log`, null, headers);
      setEntries(Array.isArray(r?.entries) ? r.entries : []);
    } catch (_) { setEntries([]); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, [targetUserId]);

  async function save() {
    if (!draft.topic.trim() || !draft.summary.trim()) { setSavingMsg('Topic and summary are required.'); return; }
    setSavingMsg('Saving…');
    try {
      const payload = {
        conversation_date: draft.conversation_date,
        topic: draft.topic.trim(),
        summary: draft.summary.trim(),
        action_items: draft.action_items.trim() || null,
        severity: draft.severity,
        step_label: draft.step_label.trim() || null,
        follow_up_date: draft.follow_up_date || null,
      };
      await api('POST', `/api/admin/users/${targetUserId}/coaching-log`, payload, headers);
      setSavingMsg('');
      setAdding(false);
      setDraft({ conversation_date: todayStr(),
                 topic: '', summary: '', action_items: '', severity: 'coaching',
                 step_label: '', follow_up_date: '' });
      await load();
    } catch (e) { setSavingMsg(`Failed: ${e.message || e}`); }
  }

  const SEV_COLORS = {
    coaching:        { bg: '#fafafa', fg: '#666',    bd: '#e0e0e0' },
    verbal_warning:  { bg: '#fef9ee', fg: '#8e6c33', bd: '#f5e6c8' },
    written_warning: { bg: '#fff8e6', fg: '#7a5b1a', bd: '#f0d9a3' },
    final_warning:   { bg: '#fef2f2', fg: '#a13030', bd: '#fecaca' },
    pip:             { bg: '#fef2f2', fg: '#a13030', bd: '#fecaca' },
    kudos:           { bg: '#e6f4ea', fg: '#1e7d3a', bd: '#c8e2c8' },
  };

  function fmtDate(d) {
    if (!d) return '';
    try { return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
    catch { return ''; }
  }

  return (
    <ProfileSection title="Coaching log">
      <div style={{ fontSize: 13, color: '#666', marginBottom: 10 }}>
        Manager-only record of improvement conversations, warnings, and
        progressive-discipline steps. NOT visible to {targetName || 'this employee'}.
      </div>
      {loading ? (
        <div style={{ fontSize: 12, color: '#888' }}>Loading…</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {entries.length === 0 && (
            <div style={{ fontSize: 12, color: '#888' }}>No entries yet. Click "+ Record entry" after a coaching conversation.</div>
          )}
          {entries.map(e => {
            const c = SEV_COLORS[e.severity] || SEV_COLORS.coaching;
            return (
              <div key={e.id} style={{ border: `1px solid ${c.bd}`, background: c.bg,
                                        borderRadius: 6, padding: 10, fontSize: 13 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
                              flexWrap: 'wrap', gap: 8, marginBottom: 4 }}>
                  <span>
                    <strong>{e.topic}</strong>
                    <span style={{ marginLeft: 8, fontSize: 10, padding: '1px 8px', borderRadius: 99,
                                    background: '#fff', color: c.fg, border: `1px solid ${c.bd}`,
                                    fontWeight: 600 }}>
                      {e.severity.replace(/_/g, ' ')}
                    </span>
                    {e.step_label && (
                      <span style={{ marginLeft: 6, fontSize: 11, color: c.fg, fontWeight: 600 }}>
                        · {e.step_label}
                      </span>
                    )}
                  </span>
                  <span style={{ color: '#888', fontSize: 11 }}>{fmtDate(e.conversation_date)}</span>
                </div>
                <div style={{ color: '#212121', whiteSpace: 'pre-wrap' }}>{e.summary}</div>
                {e.action_items && (
                  <div style={{ marginTop: 6, padding: '6px 8px', background: '#fff',
                                  borderLeft: '3px solid var(--gold)', borderRadius: 4, fontSize: 12 }}>
                    <strong>Action items:</strong> {e.action_items}
                  </div>
                )}
                {e.follow_up_date && (
                  <div style={{ marginTop: 4, fontSize: 11, color: '#888' }}>
                    Follow up by {fmtDate(e.follow_up_date)}
                  </div>
                )}
                {e.recorded_by_name && (
                  <div style={{ marginTop: 4, fontSize: 10, color: '#9e9e9e' }}>
                    Recorded by {e.recorded_by_name}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      {!adding && (
        <button onClick={() => setAdding(true)}
          style={{ marginTop: 10, padding: '6px 14px', fontSize: 13, fontWeight: 600,
                    border: '1px solid var(--gold)', background: '#fff', color: '#212121',
                    borderRadius: 6, cursor: 'pointer' }}>
          + Record entry
        </button>
      )}
      {adding && (
        <div style={{ marginTop: 12, padding: 12, background: '#fafafa', border: '1px solid #e0e0e0', borderRadius: 8 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 8, marginBottom: 10 }}>
            <label style={{ fontSize: 12, color: '#555', display: 'flex', flexDirection: 'column', gap: 4 }}>
              Date
              <input type="date" value={draft.conversation_date}
                onChange={e => setDraft(d => ({ ...d, conversation_date: e.target.value }))}
                style={{ border: '1px solid #d0d4d9', borderRadius: 6, padding: 6, fontSize: 13 }} />
            </label>
            <label style={{ fontSize: 12, color: '#555', display: 'flex', flexDirection: 'column', gap: 4 }}>
              Severity
              <select value={draft.severity}
                onChange={e => setDraft(d => ({ ...d, severity: e.target.value }))}
                style={{ border: '1px solid #d0d4d9', borderRadius: 6, padding: 6, fontSize: 13 }}>
                <option value="coaching">Coaching</option>
                <option value="kudos">Kudos</option>
                <option value="verbal_warning">Verbal warning</option>
                <option value="written_warning">Written warning</option>
                <option value="final_warning">Final warning</option>
                <option value="pip">Performance Improvement Plan</option>
              </select>
            </label>
            <label style={{ fontSize: 12, color: '#555', display: 'flex', flexDirection: 'column', gap: 4 }}>
              Step label (optional)
              <input type="text" value={draft.step_label}
                onChange={e => setDraft(d => ({ ...d, step_label: e.target.value }))}
                placeholder="e.g. Step 2 of 3"
                style={{ border: '1px solid #d0d4d9', borderRadius: 6, padding: 6, fontSize: 13, fontFamily: 'inherit' }} />
            </label>
            <label style={{ fontSize: 12, color: '#555', display: 'flex', flexDirection: 'column', gap: 4 }}>
              Follow-up by (optional)
              <input type="date" value={draft.follow_up_date}
                onChange={e => setDraft(d => ({ ...d, follow_up_date: e.target.value }))}
                style={{ border: '1px solid #d0d4d9', borderRadius: 6, padding: 6, fontSize: 13 }} />
            </label>
          </div>
          <label style={{ fontSize: 12, color: '#555', display: 'block', marginBottom: 4 }}>Topic</label>
          <input type="text" value={draft.topic}
            onChange={e => setDraft(d => ({ ...d, topic: e.target.value }))}
            placeholder="e.g. Tardiness, customer interaction, accuracy on bar"
            style={{ width: '100%', border: '1px solid #d0d4d9', borderRadius: 6, padding: 8, fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box', marginBottom: 8 }} />
          <label style={{ fontSize: 12, color: '#555', display: 'block', marginBottom: 4 }}>What was discussed</label>
          <textarea value={draft.summary}
            onChange={e => setDraft(d => ({ ...d, summary: e.target.value }))}
            rows={4}
            placeholder="Specific examples, what was said, your tone, their response."
            style={{ width: '100%', border: '1px solid #d0d4d9', borderRadius: 6, padding: 8, fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box', marginBottom: 8 }} />
          <label style={{ fontSize: 12, color: '#555', display: 'block', marginBottom: 4 }}>Action items / commitments (optional)</label>
          <textarea value={draft.action_items}
            onChange={e => setDraft(d => ({ ...d, action_items: e.target.value }))}
            rows={2}
            placeholder="What did they agree to do differently? Specific + measurable if possible."
            style={{ width: '100%', border: '1px solid #d0d4d9', borderRadius: 6, padding: 8, fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box' }} />
          <div style={{ marginTop: 10, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            {savingMsg && <span style={{ fontSize: 12, color: savingMsg.startsWith('Failed') ? '#b00020' : '#666', alignSelf: 'center' }}>{savingMsg}</span>}
            <button onClick={() => { setAdding(false); setSavingMsg(''); }}
              style={{ fontSize: 13, padding: '6px 14px', border: '1px solid #d0d4d9', background: '#fff', borderRadius: 6, cursor: 'pointer' }}>Cancel</button>
            <button onClick={save}
              style={{ fontSize: 13, padding: '6px 16px', border: 0, background: 'var(--gold)', color: '#fff', fontWeight: 600, borderRadius: 6, cursor: 'pointer' }}>
              Save entry
            </button>
          </div>
        </div>
      )}
    </ProfileSection>
  );
}
